/* Krelune learning funnel. Answers are held in this element only: no storage or analytics. */
(() => {
  'use strict';
  const define = (name, component) => { if (!customElements.get(name)) customElements.define(name, component); };
  define('kr-habit-finder', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.copy = JSON.parse(this.querySelector('[data-quiz-copy]').textContent);
      this.form = this.querySelector('form');
      this.steps = [...this.querySelectorAll('[data-quiz-step]')];
      this.result = this.querySelector('[data-quiz-result]');
      this.step = 0;
      this.classList.add('kr-enhanced');
      this.form.addEventListener('submit', e => this.next(e), { signal: this.abort.signal });
      this.querySelector('[data-quiz-back]').addEventListener('click', () => { this.step = Math.max(0, this.step - 1); this.render(true); }, { signal: this.abort.signal });
      this.querySelector('[data-quiz-reset]').addEventListener('click', () => this.reset(), { signal: this.abort.signal });
      this.form.addEventListener('change', () => { this.querySelector('[data-quiz-error]').hidden = true; }, { signal: this.abort.signal });
      document.addEventListener('kr:audience', e => this.chooseAudience(e.detail), { signal: this.abort.signal });
      const audience = new URL(location.href).searchParams.get('for') || this.dataset.defaultAudience;
      if (['self', 'partner', 'parent'].includes(audience)) this.chooseAudience(audience, false);
      this.render();
    }
    chooseAudience(value, focus = true) {
      if (!['self', 'partner', 'parent'].includes(value)) return;
      this.form.reset();
      this.form.elements.audience.value = value;
      this.step = 0;
      this.form.hidden = false;
      this.result.hidden = true;
      this.render(focus);
    }
    render(focus = false) {
      this.steps.forEach((field, i) => { field.hidden = i !== this.step; });
      this.querySelector('[data-step-label]').textContent = this.copy.step.replace('[step]', String(this.step + 1));
      this.querySelector('[data-step-bar]').style.width = ((this.step + 1) / 3 * 100) + '%';
      this.querySelector('[data-quiz-next]').textContent = this.step === 2 ? this.copy.finish : this.copy.next;
      this.querySelector('[data-quiz-back]').hidden = this.step === 0;
      this.querySelector('[data-quiz-error]').hidden = true;
      if (focus) { const legend = this.steps[this.step].querySelector('legend'); legend.tabIndex = -1; legend.focus({ preventScroll: true }); }
    }
    next(e) {
      e.preventDefault();
      const answer = this.steps[this.step].querySelector('input:checked');
      if (!answer) { this.querySelector('[data-quiz-error]').hidden = false; this.steps[this.step].querySelector('input').focus(); return; }
      if (this.step < 2) { this.step++; this.render(true); return; }
      const a = new FormData(this.form);
      const audience = a.get('audience'), moment = a.get('moment'), area = a.get('area');
      const fit = area === 'sore' ? 'sore' : audience === 'parent' ? 'parent' : area === 'nails' ? 'nails' : 'skin';
      this.querySelector('[data-result-title]').textContent = this.copy.titles[audience];
      this.querySelector('[data-result-copy]').textContent = this.copy.bodies[audience];
      this.querySelector('[data-result-action]').textContent = this.copy.actions[moment];
      this.querySelector('[data-result-fit]').textContent = this.copy.fit[fit];
      this.querySelector('[data-result-product]').hidden = fit !== 'skin';
      this.form.hidden = true;
      this.result.hidden = false;
      this.result.focus({ preventScroll: true });
    }
    reset() {
      this.form.reset(); this.step = 0; this.form.hidden = false; this.result.hidden = true; this.render(true);
    }
    disconnectedCallback() { this.abort?.abort(); }
  });

  document.addEventListener('click', event => {
    const link = event.target.closest('[data-kr-audience]');
    const finder = document.querySelector('kr-habit-finder');
    if (!link || !finder) return;
    event.preventDefault();
    // Only the non-sensitive audience route enters the URL. Quiz answers never do.
    const url = new URL(location.href); url.searchParams.set('for', link.dataset.krAudience); url.hash = 'kr-start';
    history.replaceState(null, '', url);
    document.dispatchEvent(new CustomEvent('kr:audience', { detail: link.dataset.krAudience }));
    document.getElementById('kr-start')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });

  define('kr-review-stories', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.button = this.querySelector('button');
      this.extra = [...this.querySelectorAll('[data-extra-review]')];
      if (!this.button) return;
      this.extra.forEach(item => item.hidden = true);
      this.classList.add('kr-collapsed');
      this.button.addEventListener('click', () => {
        const expanded = this.button.getAttribute('aria-expanded') !== 'true';
        if (!expanded) this.querySelector('.kr-review-stack').scrollIntoView({ block: 'start' });
        this.extra.forEach(item => item.hidden = !expanded);
        this.button.setAttribute('aria-expanded', String(expanded));
        this.button.textContent = expanded ? this.button.dataset.lessLabel : this.button.dataset.moreLabel;
        this.classList.toggle('kr-collapsed', !expanded);
      }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); }
  });

  // Offer cards and the sticky selector share the native product-info request controller.
  // A later selection cancels the earlier request; prices, stock and IDs update together.
  window.KreluneSelectVariant = async (info, id, sectionId) => {
    const form = info.productForm;
    const submit = form?.querySelector('[name=add]');
    if (!submit) return false;
    info.abortController?.abort();
    form.handleErrorMessage();
    const controller = new AbortController();
    info.abortController = controller;
    const originalLabel = submit.querySelector('span')?.textContent;
    submit.setAttribute('aria-disabled', 'true');
    info.querySelector('kr-offer-picker')?.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(info.dataset.url, location.origin);
      url.searchParams.set('variant', id); url.searchParams.set('section_id', sectionId);
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Variant unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const variant = info.getSelectedVariant(html);
      if (!variant || String(variant.id) !== String(id)) throw new Error('Variant unavailable');
      if (controller.signal.aborted || info.abortController !== controller) return false;
      info.handleUpdateProductInfo(info.dataset.url)(html);
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        form.handleErrorMessage(window.variantStrings?.unavailable || 'Please choose your option again.');
        if (originalLabel && submit.querySelector('span')) submit.querySelector('span').textContent = originalLabel;
      }
      return false;
    } finally {
      if (info.abortController === controller) {
        submit.removeAttribute('aria-disabled');
        info.querySelector('kr-offer-picker')?.removeAttribute('aria-busy');
        info.querySelector('kr-offer-picker')?.sync();
        info.querySelector('kr-sticky-buy')?.sync();
      }
    }
  };

  define('kr-offer-picker', class extends HTMLElement {
    async connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;
      await Promise.all([customElements.whenDefined('product-info'), customElements.whenDefined('product-form')]);
      if (!this.isConnected || signal.aborted) return;
      this.info = this.closest('product-info');
      if (!this.info?.productForm || typeof subscribe !== 'function') return;
      this.info.classList.add('kr-has-offers'); this.classList.add('kr-enhanced');
      this.addEventListener('change', async event => {
        if (!event.target.matches('input[type=radio]') || event.target.disabled) return;
        await window.KreluneSelectVariant(this.info, event.target.value, this.dataset.sectionId);
        this.sync();
      }, { signal: this.abort.signal });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== this.dataset.sectionId) return;
        const incoming = data.html.querySelector('kr-offer-picker');
        if (incoming) this.innerHTML = incoming.innerHTML;
        this.sync();
      });
      this.sync();
    }
    sync() {
      const id = this.info?.productForm?.querySelector('[name=id]')?.value;
      this.querySelectorAll('input[type=radio]').forEach(input => input.checked = input.value === id);
    }
    disconnectedCallback() { this.abort?.abort(); this.unsubscribe?.(); this.info?.classList.remove('kr-has-offers'); }
  });
})();
